import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  Group,
  Input,
  Select,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import {
  IconActivity,
  IconEdit,
  IconEye,
  IconPlus,
  IconSearch,
  IconTrash,
} from "@tabler/icons-react";
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_PaginationState,
  MRT_RowSelectionState,
  useMantineReactTable,
} from "mantine-react-table";
import React, { useEffect, useState } from "react";
import { repositoryMdm, styleTableMantine } from "../../_base/_const/_constVar";
import BreadcrumbLayout1 from "../../_base/component/_layout/_breadcrumb";
import { _localization } from "../../config/location";
import CreateReasonForReturn from "./CreateReasonForReturn";
import DeleteReasonForReturn from "./DeleteReasonForReturn";
import { NotificationExtension } from "../../_base/extension/NotificationExtension";
import { IconHandStop } from "@tabler/icons-react";
import { MessageResponse } from "../../model/MessageResponse";

interface SearchCriteria {
  keySearch?: string;
  level?: string;
  statusFilter?: string; // Thêm trường lọc trạng thái
}

interface ReasonForReturnEntity {
  id: number;
  code: string;
  name: string;
  active: boolean;
  createBy: number;
  createDate: string;
  lastUpdateBy: number;
  lastUpdateDate: string;
  updateBy: number;
  updateDate: string;
}
const ReasonForReturn = () => {
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [isRefetching, setIsRefetching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [height, setHeight] = useState(0);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [data, setData] = useState<ReasonForReturnEntity[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [selectIds, setSelectIds] = useState<number[]>([]);

  const normalizeString = (str: string = ""): string => {
    return str
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // Bỏ dấu
      .replace(/đ/g, "d")
      .replace(/Đ/g, "D")
      .toLowerCase()
      .trim()
      .replace(/\s+/g, " ");
  };

  const form = useForm<SearchCriteria>({
    mode: "uncontrolled",
    initialValues: {},
  });

  const filteredData = React.useMemo(() => {
    const keyword = normalizeString(form.getValues().keySearch || "");
    const statusFilter = form.getValues().statusFilter;
    let result = data;
    if (keyword) {
      result = result.filter((item) =>
        normalizeString(item.name).includes(keyword)
      );
    }
    if (statusFilter === "true") {
      result = result.filter((item) => item.active === true);
    } else if (statusFilter === "false") {
      result = result.filter((item) => item.active === false);
    }
    return result;
  }, [data, form.getValues().keySearch, form.getValues().statusFilter]);

  const columns = React.useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        header: "STT",
        accessorKey: "STT",
        enableColumnActions: false,
        enableSorting: false,
        enableResizing: false,
        Cell: ({ row }) => (
          <Flex justify={"center"} align="center">
            <Text>{row?.index + 1}</Text>
          </Flex>
        ),
        size: 100,
      },
      {
        accessorKey: "code",
        header: "Mã lý do trả hàng",
        size: 250,
        Cell: ({ renderedCellValue, cell, row }) => (
          <>
            <Badge variant="light" color="gray">
              {renderedCellValue}
            </Badge>
          </>
        ),
      },
      {
        accessorKey: "name",
        header: "Lý do trả hàng",
        size: 350,
        Cell: ({ renderedCellValue, cell, row }) => (
          <>
            <Badge variant="light" color="violet">
              {renderedCellValue}
            </Badge>
          </>
        ),
      },
      {
        accessorKey: "active",
        header: "Trạng thái",
        size: 180,
        Cell: ({ row }) => {
          const isActive = row.original.active;
          return (
            <Badge variant="light" color={isActive ? "green" : "red"}>
              {isActive ? "Đang hoạt động" : "Không hoạt động"}
            </Badge>
          );
        },
      },
      {
        accessorKey: "action",
        header: "Thao tác",
        enableSorting: false,
        enableColumnActions: false,
        enableColumnFilter: false,
        enableResizing: false,
        size: 175,
        Cell: ({ renderedCellValue, row, table }) => (
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <Tooltip label="Chỉnh sửa">
              <ActionIcon
                variant="light"
                aria-label="Settings"
                color="orange"
                // onClick={async () => {
                //   createItem(
                //     typeof row.original.id === "number"
                //       ? row.original.id
                //       : undefined
                //   );
                // }}
                onClick={async () => {
                  createItem(row.original);
                }}
              >
                <IconEdit size={20} stroke={1.5} />
              </ActionIcon>
            </Tooltip>

            <Tooltip label="Xóa">
              <ActionIcon
                variant="light"
                aria-label="Settings"
                color="red"
                disabled={row.original.active}
                onClick={async () => {
                  await deleteData([row.original.id]);
                }}
              >
                <IconTrash size={20} stroke={1.5} />
              </ActionIcon>
            </Tooltip>
            <Tooltip
              label={row.original.active === true ? "Dừng sử dụng" : "Sử dụng"}
            >
              <ActionIcon
                variant="light"
                aria-label="Settings"
                color={row.original.active === true ? "violet" : "green"}
                onClick={async () => {
                  await handleActiveStatus(row.original);
                }}
              >
                {row.original.active === true ? (
                  <IconHandStop size={20} />
                ) : (
                  <IconActivity size={20} />
                )}
              </ActionIcon>
            </Tooltip>
          </Box>
        ),
      },
    ],
    []
  );

  // active lại trạng thái
  const handleActiveStatus = async (dataSubmit: ReasonForReturnEntity) => {
    console.log("dataSubmit", dataSubmit);
    const dataApi = await repositoryMdm.post<
      MessageResponse<ReasonForReturnEntity>
    >(`/api/v1/TblDmReturnReason/update`, {
      ...dataSubmit,
      active: !dataSubmit.active,
    });
    if (dataApi?.success) {
      setTimeout(() => {
        NotificationExtension.Success("Cập nhật thành công !");
      }, 1000);
      await fetchData();
    } else {
      NotificationExtension.Fails("Đã xảy ra lỗi khi cập nhật !");
    }
  };

  //fetchData
  const fetchData = async () => {
    setIsLoading(true);
    setIsRefetching(true);
    setIsError(false);

    let url = `?Skip=${pagination?.pageIndex * pagination?.pageSize}&Take=${
      pagination.pageSize
    }`;
    try {
      const response = await repositoryMdm.get(
        `/api/v1/TblDmReturnReason/get-all${url}`
      );
      if (response && response.success) {
        let result = response.data;
        setData(result);
        setRowCount(response.totalCount);
        table.resetRowSelection();
      }
    } catch (error) {
      setData([]);
      setIsError(true);
      console.error(error);
      return;
    } finally {
      setIsLoading(false);
      setIsRefetching(false);
    }
  };

  const table = useMantineReactTable({
    columns,
    data: filteredData,
    enableColumnResizing: true,
    rowCount,
    enableRowSelection: (row) => !row.original.active, // Chỉ cho phép chọn nếu không hoạt động
    enableColumnFilters: true,
    enableSorting: true,
    enableColumnActions: true,
    // manualPagination: true,
    state: {
      // columnFilters,
      // globalFilter,
      isLoading,
      pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      showSkeletons: isLoading,
      rowSelection,
    },
    enableStickyFooter: true,
    manualFiltering: false,
    manualPagination: true,
    manualSorting: false,
    onPaginationChange: setPagination,

    positionToolbarAlertBanner: "bottom",
    initialState: {
      showColumnFilters: false,
      columnPinning: {
        left: ["mrt-row-select", "STT"],
        right: ["action"],
      },
      columnVisibility: { id: false },
      density: "xs",
    },
    mantineTopToolbarProps: {
      style: {
        display: "none",
      },
    },
    localization: _localization,
    getRowId: (row) => row.departmentId,
    mantineTableContainerProps: {
      style: { maxHeight: height, minHeight: height },
    },
    enableStickyHeader: true,
    onRowSelectionChange: setRowSelection,
    mantineTableBodyCellProps: ({ row }) => ({
      style: styleTableMantine,
    }),
    mantineToolbarAlertBannerProps: false
      ? { color: "red", children: "Lỗi tải dữ liệu !" }
      : undefined,

    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      radius: "xl",
      size: "lg",
      rowsPerPageOptions: ["20", "50", "100"],
    },
    paginationDisplayMode: "pages",
    enableColumnPinning: true,
    mantineTableProps: {
      striped: false,
    },
    mantineTableBodyRowProps: ({ row }) => ({
      onClick: row.getToggleSelectedHandler(),
      sx: { cursor: "pointer" },
    }),
  });

  const createItem = (itemDetail?: ReasonForReturnEntity | undefined) => {
    modals.openConfirmModal({
      title: (
        <>
          {itemDetail ? (
            <Title order={5}>Chỉnh sửa lý do trả hàng!</Title>
          ) : (
            <Title order={5}>Tạo mới lý do trả hàng!</Title>
          )}
        </>
      ),
      size: "auto",
      children: (
        <CreateReasonForReturn onReload={fetchData} itemDetail={itemDetail} />
      ),
      confirmProps: { display: "none" },
      cancelProps: { display: "none" },
    });
  };

  const deleteData = async (ids: (string | number)[]) => {
    if (ids.length < 1) NotificationExtension.Warn("Bạn chưa chọn dữ liệu !");
    else
      try {
        modals.openConfirmModal({
          title: (
            <>
              <Title order={5}>Bạn chắc chắn muốn xóa lý do trả hàng!</Title>
            </>
          ),

          size: "550px",
          children: (
            <DeleteReasonForReturn idItems={ids} onReload={fetchData} />
          ),
          confirmProps: { display: "none" },
          cancelProps: { display: "none" },
        });
      } catch (error) {
        console.error(error);
        return;
      }
  };

  // useEffect(() => {
  //   const valuesList = Object.keys(rowSelection);
  //   setSelectIds(valuesList);
  //   if (valuesList.length < 1) setSelectIds([]);
  //   else {
  //     const valuesList = Object.keys(rowSelection);
  //     setSelectIds(valuesList);
  //   }
  // }, [rowSelection]);
  useEffect(() => {
    // Lấy danh sách id của các hàng được chọn
    const selectedIds = Object.keys(rowSelection)
      .map((key) => {
        // Tìm đúng item theo index (key) và lấy id
        const item = filteredData[Number(key)];
        return item?.id;
      })
      .filter((id) => id !== undefined);

    setSelectIds(selectedIds as number[]);
  }, [rowSelection, filteredData]);
  console.log("selectIds", selectIds);
  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const handleResize = () => {
      setHeight(window.innerHeight - (180 + headerHeight));
    };

    handleResize(); // Set initial height
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [height]);
  useEffect(() => {
    fetchData();
  }, [pagination]);
  return (
    <Box>
      <div style={{ border: "1px solid #dee2e6" }} ref={headerRef}>
        <Group gap={0} justify="space-between">
          <BreadcrumbLayout1></BreadcrumbLayout1>
          <Flex
            gap="md"
            justify="flex-end"
            align="center"
            direction="row"
            wrap="wrap"
          >
            <Button
              leftSection={<IconPlus size={14} />}
              color="blue"
              variant="outline"
              onClick={async () => {
                createItem();
              }}
            >
              Thêm mới
            </Button>
            <Button
              leftSection={<IconTrash size={14} />}
              color="red"
              variant="outline"
              onClick={async () => {
                await deleteData(selectIds);
              }}
              disabled={selectIds.length < 1}
            >
              Xóa (đã chọn)
            </Button>
          </Flex>
        </Group>
        <Grid
          style={{
            margin: "10px",
          }}
        >
          <Grid.Col
            style={{
              display: "flex",
              gap: "10px",
              alignItems: "center",
              flexWrap: "wrap",
            }}
            span={7}
          >
            <Input
              placeholder="Nhập tìm kiếm"
              key={form.key("keySearch")}
              type="text"
              style={{ flex: 1, maxWidth: "200px" }}
              leftSection={<IconSearch size={"20"} color="#15aabf" />}
              {...form.getInputProps("keySearch")}
            />

            <Select
              placeholder="Lọc trạng thái"
              data={[
                { value: "true", label: "Đang hoạt động" },
                { value: "false", label: "Không hoạt động" },
              ]}
              {...form.getInputProps("statusFilter")}
            />
            <Button
              color="blue"
              variant="outline"
              onClick={async () => {
                await fetchData();
              }}
            >
              Tìm kiếm
            </Button>
          </Grid.Col>
        </Grid>
      </div>
      <Box mt={10}>
        <MantineReactTable table={table} />
      </Box>
    </Box>
  );
};

export default ReasonForReturn;
